import { Entity } from '@api';
import { TableCell, TableHeader } from '@design/components/table';
import { TanStackTableRoot } from '@design/components/tanstack-table/TanStackTable';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ConnectionsEmptyOrError } from '@terminal/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useThemeDrawer } from '@terminal/widgets/theme-widget/theme-drawer/useThemeDrawer';
import clsx from 'clsx';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LoaderCell } from './loader-cell/LoaderCell';
import { RankingMethodologySection } from './ranking-methodology-section/RankingMethodologySection';
import styles from './ThemeExposureTable.module.scss';
import { getColumns, RANK_COLUMN_ID } from './utils/getColumns';

interface ThemeExposureTableProps {
  entity: Entity;
}

export const ThemeExposureTable = ({ entity }: ThemeExposureTableProps) => {
  const { t } = useTranslation(['widget']);
  const { openDrawer } = useThemeDrawer();
  const { trackThemeOverviewDrawerOpen } = usePosthogEvents();

  const containerRef = useRef<HTMLDivElement>(null);
  const {
    data = [],
    isSuccess,
    isError,
    isFetching,
  } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const isEmpty = isSuccess && !isFetching && data.length === 0;
  const columns = getColumns({ t });
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return (
      <div className={styles.stateRoot}>
        <ConnectionsEmptyOrError variant="error" errorSize="fullView" />
        <RankingMethodologySection rankingType="companyToTheme" />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={styles.stateRoot}>
        <ConnectionsEmptyOrError
          variant="empty"
          layout="fillContainer"
          imageSize="full"
          title={t(
            'widget:assetOverview.knowledgeGraphRevamp.empty.themeExposure'
          )}
        />
        <RankingMethodologySection rankingType="companyToTheme" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(styles.tableWrapper, {
        [styles.loading]: isFetching,
        [styles.notLoading]: !isFetching,
      })}
    >
      <TanStackTableRoot data-testid="theme-exposure-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <TableHeader
                    key={header.id}
                    className={styles.styledTableHeader}
                    data-table-header
                    alignment="left"
                    isHoverEnabled={false}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <LoaderCell numberOfRows={3} />
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <TableCell
                      key={cell.id}
                      className={styles.styledTableCell}
                      data-table-cell
                      alignment="left"
                      isNumber={cell.column.id === RANK_COLUMN_ID}
                      onClick={() => {
                        trackThemeOverviewDrawerOpen(
                          POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                          [row.original.theme.id]
                        );
                        openDrawer(row.original.theme.id);
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))
          )}
          <tr data-testid="footer-row">
            <TableCell
              className={styles.footerRow}
              data-footer-row
              colspan={table.getHeaderGroups()[0].headers.length}
              isNumber={false}
            >
              <RankingMethodologySection rankingType={'companyToTheme'} />
            </TableCell>
          </tr>
        </tbody>
      </TanStackTableRoot>
    </div>
  );
};
